import type { PageRef, Rotation, SourceInfo } from '@/types'
import { uid } from '../id'
import { openPdf, type PDFDocumentProxy } from './pdfjs'

export interface LoadedSource {
  info: SourceInfo
  bytes: Uint8Array
  pdf: PDFDocumentProxy
  password?: string
}

/**
 * Live pdf.js documents, keyed by source id. Kept outside React state on purpose: these are
 * big, mutable, non-serializable objects.
 */
const sources = new Map<string, LoadedSource>()

export const getSource = (id: string | null) => (id ? sources.get(id) : undefined)
export const allSources = () => [...sources.values()]

export async function loadSource(
  bytes: Uint8Array,
  name: string,
  opts: { id?: string; password?: string } = {},
): Promise<{ source: LoadedSource; pages: PageRef[] }> {
  const pdf = await openPdf(bytes, opts.password)
  const info: SourceInfo = { id: opts.id ?? uid('src'), name, pageCount: pdf.numPages }
  const source: LoadedSource = { info, bytes, pdf, password: opts.password }
  sources.set(info.id, source)
  const pages = await readPageRefs(source)
  return { source, pages }
}

async function readPageRefs(source: LoadedSource): Promise<PageRef[]> {
  const { pdf, info } = source
  const out: PageRef[] = new Array(pdf.numPages)
  const BATCH = 24
  for (let start = 0; start < pdf.numPages; start += BATCH) {
    const jobs: Promise<void>[] = []
    for (let i = start; i < Math.min(pdf.numPages, start + BATCH); i++) {
      jobs.push(
        pdf.getPage(i + 1).then((p) => {
          const vp = p.getViewport({ scale: 1 })
          out[i] = {
            id: uid('pg'),
            srcId: info.id,
            srcIndex: i,
            width: vp.width,
            height: vp.height,
            view: p.view as [number, number, number, number],
            baseRotation: (((p.rotate % 360) + 360) % 360) as Rotation,
            rotation: 0,
          }
        }),
      )
    }
    await Promise.all(jobs)
  }
  return out
}

export function blankPage(width = 612, height = 792): PageRef {
  return {
    id: uid('pg'),
    srcId: null,
    srcIndex: 0,
    width,
    height,
    view: [0, 0, width, height],
    baseRotation: 0,
    rotation: 0,
  }
}

export async function closeAllSources() {
  const all = [...sources.values()]
  sources.clear()
  await Promise.allSettled(all.map((s) => s.pdf.loadingTask.destroy()))
}
